import {
  PageBlueprint,
  createFrontendPlugin,
  createRouteRef,
} from '@backstage/frontend-plugin-api';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';

/**
 * Frontend plugin for `/notifications-admin`.
 *
 * Why a dedicated page (not a tab inside the upstream
 * `@backstage/plugin-notifications` page):
 *
 *   The upstream notifications page is owned by `page:notifications`
 *   and provides its own loader. Overriding it would require us to
 *   re-implement the inbox / filter / mark-as-read surface — which
 *   is exactly what we DON'T want to duplicate. So we keep them as
 *   sibling routes: `/notifications` is the inbox (every user),
 *   `/notifications-admin` is the admin surface (admins only). The
 *   admin page has an "Inbox" tab that DEEP-LINKS to /notifications
 *   to make the relationship discoverable.
 *
 * Sidebar wiring: `PageBlueprint` auto-discovers a sidebar nav item
 * from `title` + `icon` + `routeRef` when the page lives inside a
 * plugin (the framework looks at `extension.spec.plugin?.id`). For
 * admin-only visibility we COULD wrap with a NavItem blueprint that
 * gates on the permission, but the page itself shows an "access
 * denied" panel for non-admins which is a sufficient UX gate —
 * trying to hide the sidebar item entirely runs into framework
 * limitations (NavItemBlueprint runs outside the usePermission
 * render boundary). See `permissionsModule` for the same trade-off.
 *
 * `noHeader: true` is REQUIRED — the inner `NotificationsAdminPage`
 * already renders its own `<Header>` (the blue banner is part of
 * our custom Page layout). Without `noHeader: true` we'd stack a
 * second blue banner on top of the first. Same trick as
 * `permissionsModule` and other admin modules.
 */
const notificationsAdminRouteRef = createRouteRef();

const notificationsAdminPage = PageBlueprint.make({
  params: {
    path: '/notifications-admin',
    title: 'Notification admin',
    icon: <NotificationsActiveIcon />,
    routeRef: notificationsAdminRouteRef,
    noHeader: true,
    loader: () =>
      import('../../components/notificationsAdmin/NotificationsAdminPage').then(
        m => <m.NotificationsAdminPage />,
      ),
  },
});

export const notificationsAdminModule = createFrontendPlugin({
  pluginId: 'notifications-admin',
  routes: {
    notificationsAdmin: notificationsAdminRouteRef,
  },
  extensions: [notificationsAdminPage],
});
